import { useContext, useMemo, useState, type ChangeEvent } from "react";
import { DayPicker, type DropdownProps } from "react-day-picker";

import { cn } from "../lib/cn";
import { useUiStrings } from "../lib/strings";
import { Button } from "./Button";
import { FieldIdContext } from "./FormControls";
import { Icon } from "./Icon";
import { Popover, PopoverContent, PopoverTrigger } from "./Overlays";
import { Select } from "./Select";

/*
 * Dates travel as the same strings the browser's own inputs used, so forms and APIs don't change:
 *   DatePicker      "YYYY-MM-DD"        (empty string = no date)
 *   DateTimePicker  "YYYY-MM-DDTHH:mm"  (local time; app-core's toInstant/toLocalInput convert)
 * Month and weekday names come from Intl in the app's language; digits stay Latin like the rest of the UI.
 */

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-10-11" (or the date part of "2026-10-11T09:30") → a local Date, or undefined. */
export function parseDateValue(value: string | null | undefined): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!match) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.getMonth() === Number(match[2]) - 1 ? date : undefined;
}

export function toDateValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function intlLocale(locale: string): string {
  return `${locale}-u-nu-latn`;
}

/** The accessible name of a day in the calendar, e.g. "Saturday, January 31, 2026" (tests use it too). */
export function dayLabel(date: Date, locale = "en"): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: "full" }).format(date);
}

const navButton =
  "inline-flex size-8 items-center justify-center rounded-full text-foreground hover:bg-surface-muted " +
  "disabled:pointer-events-none disabled:opacity-40";

/** Month and year jump lists in the calendar header, drawn with our own `Select` (never the system list). */
function CalendarDropdown({
  options = [],
  value,
  onChange,
  "aria-label": ariaLabel,
}: DropdownProps) {
  return (
    <Select
      aria-label={ariaLabel}
      className="h-8 w-auto min-w-20 gap-1 rounded-lg px-2.5 font-semibold"
      value={String(value)}
      options={options.map((option) => ({
        value: String(option.value),
        label: option.label,
        disabled: option.disabled,
      }))}
      onValueChange={(next) =>
        onChange?.({ target: { value: next } } as unknown as ChangeEvent<HTMLSelectElement>)
      }
    />
  );
}

export interface CalendarProps {
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  /** First and last day that may be picked. */
  min?: Date;
  max?: Date;
  className?: string;
}

/** A one-month calendar in the MOTOLYNK theme (keyboard: arrows, Page Up/Down, Home/End). */
export function Calendar({ selected, onSelect, min, max, className }: CalendarProps) {
  const strings = useUiStrings();
  const locale = intlLocale(strings.locale);
  const thisYear = new Date().getFullYear();
  const formats = useMemo(
    () => ({
      caption: new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }),
      month: new Intl.DateTimeFormat(locale, { month: "short" }),
      weekday: new Intl.DateTimeFormat(locale, { weekday: "short" }),
      full: new Intl.DateTimeFormat(locale, { dateStyle: "full" }),
    }),
    [locale],
  );
  return (
    // The month/year lists are `Select`s: they must not take the surrounding form field's id.
    <FieldIdContext.Provider value={undefined}>
      <DayPicker
        mode="single"
        required={false}
        selected={selected}
        onSelect={onSelect}
        defaultMonth={selected ?? (max && max < new Date() ? max : undefined)}
        disabled={[...(min ? [{ before: min }] : []), ...(max ? [{ after: max }] : [])]}
        startMonth={min ?? new Date(1950, 0)}
        endMonth={max ?? new Date(thisYear + 10, 11)}
        captionLayout="dropdown"
        showOutsideDays
        weekStartsOn={1}
        formatters={{
          formatCaption: (month) => formats.caption.format(month),
          formatMonthDropdown: (month) => formats.month.format(month),
          formatYearDropdown: (year) => String(year.getFullYear()),
          formatWeekdayName: (day) => formats.weekday.format(day),
        }}
        labels={{
          labelPrevious: () => strings.previousMonth,
          labelNext: () => strings.nextMonth,
          labelMonthDropdown: () => strings.month,
          labelYearDropdown: () => strings.year,
          labelDayButton: (date, modifiers) =>
            `${modifiers.today ? `${strings.today}, ` : ""}${formats.full.format(date)}`,
        }}
        components={{
          Dropdown: CalendarDropdown,
          Chevron: ({ orientation }) => (
            <Icon name={orientation === "left" ? "chevron-left" : "chevron-right"} size={16} />
          ),
        }}
        className={cn("text-sm", className)}
        classNames={{
          months: "relative",
          month: "space-y-3",
          month_caption: "flex h-8 items-center pr-18",
          caption_label: "font-semibold",
          dropdowns: "flex items-center gap-1.5",
          nav: "absolute top-0 right-0 flex items-center gap-1",
          button_previous: navButton,
          button_next: navButton,
          month_grid: "w-full border-collapse",
          weekdays: "flex",
          weekday: "w-10 text-center text-xs font-medium text-muted-foreground",
          week: "mt-1 flex",
          day: "size-10 p-0 text-center",
          day_button:
            "size-10 rounded-full text-sm outline-none hover:bg-brand-soft focus-visible:ring-2 focus-visible:ring-brand " +
            "disabled:pointer-events-none",
          selected:
            "[&>button]:bg-brand [&>button]:font-semibold [&>button]:text-brand-foreground [&>button]:hover:bg-brand-hover",
          today: "[&>button]:font-bold [&>button]:underline [&>button]:underline-offset-4",
          outside: "text-muted-foreground opacity-60",
          disabled: "text-muted-foreground opacity-40",
          hidden: "invisible",
        }}
      />
    </FieldIdContext.Provider>
  );
}

interface PickerBaseProps {
  /** Text shown when there is no value. */
  placeholder?: string;
  disabled?: boolean;
  /** Show a × to empty the field (default true). */
  clearable?: boolean;
  id?: string;
  name?: string;
  className?: string;
  onBlur?: () => void;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
}

function PickerTrigger({
  id,
  name,
  text,
  placeholder,
  disabled,
  clearable,
  onClear,
  className,
  ...aria
}: Omit<PickerBaseProps, "onBlur"> & { text: string; onClear: () => void }) {
  const strings = useUiStrings();
  return (
    <div className={cn("relative", className)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          name={name}
          disabled={disabled}
          aria-label={aria["aria-label"]}
          aria-invalid={aria["aria-invalid"]}
          aria-describedby={aria["aria-describedby"]}
          aria-haspopup="dialog"
          className={cn(
            "flex h-11 w-full items-center gap-2.5 rounded-input border border-input bg-surface px-3.5 text-left text-sm " +
              "text-foreground outline-none transition-colors focus-visible:border-foreground focus-visible:ring-2 " +
              "focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-danger",
            clearable && text && "pr-10",
          )}
        >
          <Icon name="calendar" size={16} className="shrink-0 text-muted-foreground" />
          <span className={cn("min-w-0 flex-1 truncate", !text && "text-muted-foreground")}>
            {text || placeholder}
          </span>
        </button>
      </PopoverTrigger>
      {clearable && text && !disabled && (
        <button
          type="button"
          aria-label={strings.clear}
          onClick={onClear}
          className="absolute inset-y-0 right-2 my-auto flex size-7 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}

export interface DatePickerProps extends PickerBaseProps {
  /** "YYYY-MM-DD", or empty for no date. */
  value: string | null | undefined;
  /** Called with "YYYY-MM-DD", or "" when cleared. */
  onChange: (value: string) => void;
  /** Earliest / latest date that may be picked ("YYYY-MM-DD"). */
  min?: string;
  max?: string;
}

/** Pick one day from a themed calendar (replaces `<input type="date">`). */
export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder,
  clearable = true,
  onBlur,
  id,
  ...rest
}: DatePickerProps) {
  const fieldId = useContext(FieldIdContext);
  const strings = useUiStrings();
  const [open, setOpen] = useState(false);
  const selected = parseDateValue(value);
  const minDate = parseDateValue(min);
  const maxDate = parseDateValue(max);
  const text = selected
    ? new Intl.DateTimeFormat(intlLocale(strings.locale), { dateStyle: "medium" }).format(selected)
    : "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayAllowed = (!minDate || today >= minDate) && (!maxDate || today <= maxDate);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PickerTrigger
        {...rest}
        id={id ?? fieldId}
        text={text}
        placeholder={placeholder ?? strings.pickDate}
        clearable={clearable}
        onClear={() => onChange("")}
      />
      <PopoverContent className="w-auto p-3" aria-label={strings.pickDate}>
        <Calendar
          selected={selected}
          min={minDate}
          max={maxDate}
          onSelect={(date) => {
            if (!date) return;
            onChange(toDateValue(date));
            setOpen(false);
          }}
        />
        <div className="mt-2 flex justify-end border-t border-border pt-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!todayAllowed}
            onClick={() => {
              onChange(toDateValue(today));
              setOpen(false);
            }}
          >
            {strings.today}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export interface DateTimePickerProps extends DatePickerProps {
  /** Minutes offered in the time list (default 5; an existing value's own minute is always offered). */
  minuteStep?: number;
}

const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: pad(h), label: pad(h) }));

/** Pick a day and a time (replaces `<input type="datetime-local">`); value "YYYY-MM-DDTHH:mm" in local time. */
export function DateTimePicker({
  value,
  onChange,
  min,
  max,
  placeholder,
  clearable = true,
  onBlur,
  id,
  minuteStep = 5,
  ...rest
}: DateTimePickerProps) {
  const fieldId = useContext(FieldIdContext);
  const strings = useUiStrings();
  const [open, setOpen] = useState(false);
  const selected = parseDateValue(value);
  const time = /T(\d{2}):(\d{2})/.exec(value ?? "");
  const hour = time?.[1] ?? "09";
  const minute = time?.[2] ?? "00";
  const minutes = useMemo(() => {
    const list = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => pad(i * minuteStep));
    if (!list.includes(minute)) list.push(minute);
    return list.sort().map((m) => ({ value: m, label: m }));
  }, [minuteStep, minute]);
  const text = selected
    ? new Intl.DateTimeFormat(intlLocale(strings.locale), {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(
        new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), +hour, +minute),
      )
    : "";
  const emit = (date: Date, h: string, m: string) => onChange(`${toDateValue(date)}T${h}:${m}`);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PickerTrigger
        {...rest}
        id={id ?? fieldId}
        text={text}
        placeholder={placeholder ?? strings.pickDateTime}
        clearable={clearable}
        onClear={() => onChange("")}
      />
      <PopoverContent className="w-auto p-3" aria-label={strings.pickDateTime}>
        <Calendar
          selected={selected}
          min={parseDateValue(min)}
          max={parseDateValue(max)}
          onSelect={(date) => date && emit(date, hour, minute)}
        />
        <FieldIdContext.Provider value={undefined}>
          <div className="mt-2 flex items-center gap-2 border-t border-border pt-3">
            <Icon name="clock" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium">{strings.time}</span>
            <Select
              aria-label={strings.hours}
              className="h-9 w-20"
              options={HOURS}
              value={hour}
              disabled={!selected}
              onValueChange={(h) => selected && emit(selected, h, minute)}
            />
            <span aria-hidden>:</span>
            <Select
              aria-label={strings.minutes}
              className="h-9 w-20"
              options={minutes}
              value={minute}
              disabled={!selected}
              onValueChange={(m) => selected && emit(selected, hour, m)}
            />
            <Button size="sm" className="ml-auto" onClick={() => setOpen(false)}>
              {strings.done}
            </Button>
          </div>
        </FieldIdContext.Provider>
      </PopoverContent>
    </Popover>
  );
}
